import type { Injector, Signal } from '@angular/core'
import { Injectable, inject } from '@angular/core'
import type { WorkType } from '@awork/features/task/models/work-type.model'
import { WorkTypeStore } from '@awork/features/task/state/work-type.store'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { Project } from '@awork/features/project/models/project.model'

@Injectable({ providedIn: 'root' })
export class WorkTypeQuery extends EntitySignalQuery<WorkType> {
  protected store: WorkTypeStore

  constructor() {
    const store = inject(WorkTypeStore)

    super(store)

    this.store = store
  }

  /**
   * Selects all work types
   * @param {Injector} injector Angular injector
   * @param {Project} project The context of the project which is used to filter work types
   * @param {boolean} [selectArchived=false] True to also select archived work types
   * @returns {Observable<WorkType[]>}
   * @deprecated
   */
  selectWorkTypes(injector: Injector, project?: Project, selectArchived = false): Observable<WorkType[]> {
    const externalProjectId = project?.isExternal ? project.id : undefined

    return this.selectAll(injector, {
      filterBy: workType =>
        (!selectArchived ? !workType.isArchived : true) && this.filterByExternalProjectId(workType, externalProjectId),
      sortBy: (a, b) => this.getSort(a, b)
    }).pipe(map(workTypes => this.mapEntities(workTypes)))
  }

  /**
   * Gets all work types
   * @param {Project} project The context of the project which is used to filter work types
   * @param {boolean} [getArchived=false] True to also select archived work types
   * @returns {WorkType[]}
   */
  getWorkTypes(project?: Project, getArchived = false): WorkType[] {
    const externalProjectId = project?.isExternal ? project.id : undefined

    const workTypes = this.getAll({
      filterBy: workType =>
        (!getArchived ? !workType.isArchived : true) && this.filterByExternalProjectId(workType, externalProjectId),
      sortBy: (a, b) => this.getSort(a, b)
    })

    return this.mapEntities(workTypes)
  }

  /**
   * Gets all work types which belong to an external project
   * @returns {Signal<WorkType[]>}
   */
  queryExternalWorkTypes(): Signal<WorkType[]> {
    return this.queryAll({
      filterBy: workType => !!workType.externalProjectIds?.length,
      sortBy: (a, b) => this.getSort(a, b)
    })
  }

  /**
   * Gets all work types as a signal
   * @param {Project} project The context of the project which is used to filter work types
   * @param {boolean} [getArchived=false] True to also select archived work types
   * @returns {Signal<WorkType[]>}
   */
  queryWorkTypes(project?: Project, getArchived = false): Signal<WorkType[]> {
    const externalProjectId = project?.isExternal ? project.id : undefined

    return this.queryAll({
      filterBy: workType =>
        (!getArchived ? !workType.isArchived : true) && this.filterByExternalProjectId(workType, externalProjectId),
      sortBy: (a, b) => this.getSort(a, b)
    })
  }

  /**
   * Filters work types by external project id, when external project id is not provided,
   * it returns the work types that are not linked to any external project
   * @param {WorkType} workType
   * @param {string} externalProjectId
   * @returns {boolean}
   */
  private filterByExternalProjectId(workType: WorkType, externalProjectId: string): boolean {
    if (!externalProjectId) {
      return !workType.externalProjectIds?.length
    }

    return workType.externalProjectIds?.includes(externalProjectId)
  }

  /**
   * Gets the sort method used for the queries, which sorts by name in ascending order
   * @param {WorkType} typeOfWorkA
   * @param {WorkType} typeOfWorkB
   * @returns {number}
   * @private
   */
  private getSort(typeOfWorkA: WorkType, typeOfWorkB: WorkType): number {
    return typeOfWorkA.name.localeCompare(typeOfWorkB.name)
  }
}
